// =============================================================================
// email-shortcode-token.util.ts
// Ambivista Phase I — Tokenized Shortcodes / Merge Tags in TinyMCE Email Editors
//
// ARCHITECTURE OVERVIEW:
//   - tokenizeEditorShortcodes()  : converts {{raw}} text nodes → chip <span>s
//   - detokenizeShortcodeHtml()   : converts chip markup → {{raw}} shortcodes
//   - installEmailShortcodeTokenBehavior() : wires both into a TinyMCE editor
//   - buildShortcodeInsertHtml()  : generates token-ready HTML for insertion
//   - getShortcodeBySelectValue() / getShortcodeByRawShortcode() : lookups
//
// KEY CONTRACT:
//   The raw {{DoublebraceFormat}} is ALWAYS what gets saved/sent to the backend.
//   Chip markup is editor-only and must NEVER reach the API payload.
// =============================================================================

// ---------------------------------------------------------------------------
// Registry definition
// ---------------------------------------------------------------------------

export interface EmailShortcodeDefinition {
  key: string;
  selectValue: string;
  label: string;
  shortcode: string;
  insertHtml?: string;
  isLink?: boolean;
  visibleText?: string;
  allowInSubject?: boolean;
}

export const EMAIL_SHORTCODES: EmailShortcodeDefinition[] = [
  {
    key: 'SurveyName',
    selectValue: 'survey_name',
    label: 'Survey Name',
    shortcode: '{{SurveyName}}',
  },
  {
    key: 'FirstName',
    selectValue: 'first_name',
    label: 'First Name',
    shortcode: '{{FirstName}}',
  },
  {
    key: 'SurveyLink',
    selectValue: 'survey_link',
    label: 'Survey Link',
    shortcode: '{{SurveyLink}}',
    isLink: true,
    visibleText: '{{SurveyLink}}',
    insertHtml: `<a href="{{SurveyLink}}">{{SurveyLink}}</a>`,
  },
  {
    key: 'UnsubscribeLink',
    selectValue: 'unsubscribe_link',
    label: 'Unsubscribe Link',
    shortcode: '{{UnsubscribeLink}}',
    isLink: true,
    visibleText: 'Unsubscribe Link',
    insertHtml: `<a href="{{UnsubscribeLink}}">Unsubscribe Link</a>`,
  },
  {
    key: 'SurveyExpiration',
    selectValue: 'survey_expiration',
    label: 'Survey Expiration',
    shortcode: '{{SurveyExpiration}}',
  },
  {
    key: 'SurveyExpirationIntl',
    selectValue: 'survey_expiration_intl',
    label: 'Survey Expiration (International)',
    shortcode: '{{SurveyExpirationIntl}}',
  },
  {
    key: 'DayPriorExpiration',
    selectValue: 'day_prior_expiration',
    label: 'Day Prior Expiration',
    shortcode: '{{DayPriorExpiration}}',
  },
  {
    key: 'ParticipantFullName',
    selectValue: 'ratee_name',
    label: "Participant's Name",
    shortcode: '{{ParticipantFullName}}',
    allowInSubject: true,
  },
  {
    key: 'ParticipantFirstName',
    selectValue: 'participant_first_name',
    label: "Participant's First Name",
    shortcode: '{{ParticipantFirstName}}',
  },
  {
    key: 'AdminName',
    selectValue: 'member_name',
    label: 'Signature',
    shortcode: '{{AdminName}}',
  },
];

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function getShortcodeBySelectValue(
  selectValue: string
): EmailShortcodeDefinition | undefined {
  return EMAIL_SHORTCODES.find((d) => d.selectValue === selectValue);
}

export function getShortcodeByRawShortcode(
  shortcode: string
): EmailShortcodeDefinition | undefined {
  return EMAIL_SHORTCODES.find((d) => d.shortcode === shortcode);
}

// ---------------------------------------------------------------------------
// Chip CSS — injected into TinyMCE content_style (iframe scope)
// ---------------------------------------------------------------------------

export const EMAIL_SHORTCODE_TOKEN_STYLE = `
  .amb-merge-token {
    display: inline-block;
    font-family: inherit;
    font-size: inherit;
    line-height: 1.2;
    color: #1f4e79;
    background: #eef6ff;
    border: 1px solid #b7d7f0;
    border-radius: 10px;
    padding: 1px 6px;
    margin: 0 1px;
    white-space: nowrap;
    cursor: default;
    vertical-align: baseline;
    user-select: all;
  }
  .amb-merge-link-token {
    text-decoration: none;
  }
`;

// ---------------------------------------------------------------------------
// Tooltip helper — shows "Label — {{Shortcode}}" on hover
// ---------------------------------------------------------------------------

function getShortcodeTooltip(def: EmailShortcodeDefinition): string {
  return `${def.label} \u2014 ${def.shortcode}`;
}

// ---------------------------------------------------------------------------
// Insert HTML builder
// ---------------------------------------------------------------------------

/**
 * Returns the HTML that should be inserted into the editor when a shortcode
 * is selected from the dropdown.
 *
 * For plain tokens:  a protected chip <span>.
 * For Survey Link:   an <a> with a chip as the visible text.
 * For Unsubscribe:   a protected chip-link <a>.
 */
export function buildShortcodeInsertHtml(def: EmailShortcodeDefinition): string {
  if (def.selectValue === 'survey_link') {
    // Anchor with chip-span as visible text; href stays raw
    return (
      `<a href="${def.shortcode}">` +
      `<span class="amb-merge-token mceNonEditable" contenteditable="false" ` +
      `data-amb-shortcode-token="true" data-shortcode="${def.shortcode}" ` +
      `data-label="${def.label}" title="${getShortcodeTooltip(def)}">${def.label}</span>` +
      `</a>`
    );
  }

  if (def.selectValue === 'unsubscribe_link') {
    // Entire anchor is the protected token
    return (
      `<a href="${def.shortcode}" ` +
      `class="amb-merge-token amb-merge-link-token mceNonEditable" ` +
      `contenteditable="false" data-amb-shortcode-token="true" ` +
      `data-shortcode="${def.shortcode}" data-label="${def.label}" ` +
      `title="${getShortcodeTooltip(def)}">${def.label}</a>`
    );
  }

  // Plain inline token
  return (
    `<span class="amb-merge-token mceNonEditable" contenteditable="false" ` +
    `data-amb-shortcode-token="true" data-shortcode="${def.shortcode}" ` +
    `data-label="${def.label}" title="${getShortcodeTooltip(def)}">${def.label}</span>`
  );
}

// ---------------------------------------------------------------------------
// Tokenization — operates on TinyMCE's live DOM (text nodes only)
// ---------------------------------------------------------------------------

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Collect all text nodes that contain '{{' and are not inside existing tokens. */
function collectTokenizableTextNodes(root: HTMLElement): Text[] {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Node) {
      if (!node.textContent || !node.textContent.includes('{{')) {
        return NodeFilter.FILTER_REJECT;
      }
      const parent = (node as Text).parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;

      // Skip nodes already inside a token
      if (parent.closest('[data-amb-shortcode-token="true"]')) {
        return NodeFilter.FILTER_REJECT;
      }
      // Skip non-visual content
      if (parent.closest('script, style, code, pre')) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  } as any);

  const nodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    nodes.push(node as Text);
  }
  return nodes;
}

/**
 * Replace valid shortcode occurrences in a single text node with chip elements.
 * Splits the text node around each match and inserts span elements.
 */
function replaceShortcodesInTextNode(
  textNode: Text,
  regex: RegExp,
  defMap: Map<string, EmailShortcodeDefinition>
): void {
  const text = textNode.textContent || '';
  regex.lastIndex = 0;

  const fragments: (string | HTMLElement)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      fragments.push(text.slice(lastIndex, match.index));
    }

    const def = defMap.get(match[0]);
    if (def) {
      const doc = textNode.ownerDocument;
      const span = doc.createElement('span');
      span.className = 'amb-merge-token mceNonEditable';
      span.contentEditable = 'false';
      span.setAttribute('data-amb-shortcode-token', 'true');
      span.setAttribute('data-shortcode', def.shortcode);
      span.setAttribute('data-label', def.label);
      span.setAttribute('title', getShortcodeTooltip(def));
      span.textContent = def.label;
      fragments.push(span);
    } else {
      fragments.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  // No matches — nothing to do
  if (fragments.length === 0) return;

  if (lastIndex < text.length) {
    fragments.push(text.slice(lastIndex));
  }

  const parent = textNode.parentNode;
  if (!parent) return;

  const doc = textNode.ownerDocument;
  const frag = doc.createDocumentFragment();
  for (const part of fragments) {
    if (typeof part === 'string') {
      frag.appendChild(doc.createTextNode(part));
    } else {
      frag.appendChild(part);
    }
  }
  parent.replaceChild(frag, textNode);
}

/**
 * Also protect Unsubscribe Link anchors that have a raw href but human-readable
 * visible text — these won't be caught by text-node scanning.
 */
function tokenizeUnsubscribeLinkAnchors(
  body: HTMLElement,
  def: EmailShortcodeDefinition
): void {
  const anchors = Array.from(
    body.querySelectorAll<HTMLAnchorElement>(`a[href="${def.shortcode}"]`)
  );
  for (const a of anchors) {
    // Skip already-tokenized anchors
    if (a.getAttribute('data-amb-shortcode-token') === 'true') continue;
    // Only protect anchors whose text is the human label (not raw shortcode text)
    const visibleText = (a.textContent || '').trim();
    if (visibleText === def.label || visibleText === def.visibleText) {
      a.classList.add('amb-merge-token', 'amb-merge-link-token', 'mceNonEditable');
      a.contentEditable = 'false';
      a.setAttribute('data-amb-shortcode-token', 'true');
      a.setAttribute('data-shortcode', def.shortcode);
      a.setAttribute('data-label', def.label);
      a.setAttribute('title', getShortcodeTooltip(def));
    }
  }
}

/**
 * Main tokenization entry point. Safe to call repeatedly — re-entrant guard
 * prevents infinite loops from TinyMCE change events.
 */
export function tokenizeEditorShortcodes(
  editor: any,
  definitions: EmailShortcodeDefinition[] = EMAIL_SHORTCODES
): void {
  if (!editor || !editor.getBody()) return;
  if ((editor as any).__ambTokenizing) return;
  (editor as any).__ambTokenizing = true;

  try {
    const body: HTMLElement = editor.getBody();

    // Build a fast lookup map: shortcode string → definition
    const defMap = new Map<string, EmailShortcodeDefinition>();
    for (const d of definitions) {
      defMap.set(d.shortcode, d);
    }

    // Build regex that matches any known shortcode exactly
    const pattern = definitions
      .map((d) => escapeRegExp(d.shortcode))
      .join('|');
    const regex = new RegExp(pattern, 'g');

    // 1. Tokenize text nodes (catches plain {{Token}} text)
    const textNodes = collectTokenizableTextNodes(body);
    for (const node of textNodes) {
      replaceShortcodesInTextNode(node, regex, defMap);
    }

    // 2. Protect Unsubscribe Link anchors that have a raw href
    const unsubDef = definitions.find((d) => d.selectValue === 'unsubscribe_link');
    if (unsubDef) {
      tokenizeUnsubscribeLinkAnchors(body, unsubDef);
    }
  } finally {
    (editor as any).__ambTokenizing = false;
  }
}

// ---------------------------------------------------------------------------
// Detokenization — operates on an HTML string, returns clean raw HTML
// ---------------------------------------------------------------------------

/**
 * Remove token-only attributes from a link anchor, preserving href and any
 * non-token classes/styles the author may have added.
 */
function cleanupTokenAttributes(el: HTMLElement): void {
  el.removeAttribute('contenteditable');
  el.removeAttribute('data-amb-shortcode-token');
  el.removeAttribute('data-shortcode');
  el.removeAttribute('data-label');
  el.removeAttribute('title');

  ['amb-merge-token', 'amb-merge-link-token', 'mceNonEditable'].forEach((c) =>
    el.classList.remove(c)
  );

  // Remove empty class attribute to keep markup clean
  if (el.getAttribute('class') === '') {
    el.removeAttribute('class');
  }
}

/**
 * Convert any chip/token markup back to raw {{shortcode}} HTML.
 * This must be called before saving, sending, scheduling, or showing source view.
 */
export function detokenizeShortcodeHtml(html: string): string {
  if (!html || !html.includes('data-amb-shortcode-token')) {
    return html;
  }

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement;

  root
    .querySelectorAll<HTMLElement>('[data-amb-shortcode-token="true"]')
    .forEach((el) => {
      const shortcode = el.getAttribute('data-shortcode');
      if (!shortcode) return;

      const tag = el.tagName.toLowerCase();

      if (tag === 'span') {
        // Plain text token — replace with raw shortcode text
        el.replaceWith(doc.createTextNode(shortcode));
        return;
      }

      if (tag === 'a') {
        // For Survey Link: the <span> chip is *inside* the anchor.
        // The anchor itself is a normal link — only the chip-span needs replacing.
        // Those spans are handled by the 'span' branch above when we query all tokens.
        // For Unsubscribe Link: the <a> itself is the token — strip token attrs,
        // restore plain anchor with its label text.
        cleanupTokenAttributes(el);
        // Ensure visible text is the raw label (it should already be correct)
      }
    });

  return root.innerHTML;
}

// ---------------------------------------------------------------------------
// TinyMCE behavior installer
// ---------------------------------------------------------------------------

/**
 * Wire tokenization and detokenization into a TinyMCE editor instance.
 * Call this once inside the editor's `setup` callback, after any existing
 * event registrations.
 */
export function installEmailShortcodeTokenBehavior(
  editor: any,
  definitions: EmailShortcodeDefinition[] = EMAIL_SHORTCODES
): void {
  const tokenize = () => {
    // Use setTimeout(0) to let TinyMCE finish its own DOM work first
    setTimeout(() => tokenizeEditorShortcodes(editor, definitions), 0);
  };

  editor.on('init', tokenize);
  editor.on('SetContent', tokenize);
  editor.on('PastePostProcess', tokenize);
  editor.on('Undo', tokenize);
  editor.on('Redo', tokenize);

  editor.on('keyup', () => {
    clearTimeout((editor as any).__ambTokenizeTimer);
    (editor as any).__ambTokenizeTimer = setTimeout(tokenize, 300);
  });

  // Detokenize content whenever TinyMCE reads it (includes source/code view dialog)
  editor.on('GetContent', (e: any) => {
    if (e.content) {
      e.content = detokenizeShortcodeHtml(e.content);
    }
  });

  // Belt-and-suspenders: explicitly set raw content before code view opens,
  // then re-tokenize when the command finishes (dialog closed).
  editor.on('BeforeExecCommand', (e: any) => {
    if (e.command === 'mceCodeEditor') {
      // Set raw content so the code dialog shows clean shortcodes
      const raw = detokenizeShortcodeHtml(
        editor.getContent({ no_events: true, format: 'html' })
      );
      // Temporarily suppress GetContent so we don't double-detokenize
      editor.setContent(raw, { no_events: true });
    }
  });

  editor.on('ExecCommand', (e: any) => {
    if (e.command === 'mceCodeEditor') {
      // Re-tokenize after the code dialog closes
      tokenize();
    }
  });
}
